import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from 'primereact/skeleton';
import CustomButton from '../../../shared/CustomButton';
import { FaCheckDouble, FaArrowRight, FaEye, FaEyeSlash, FaMobileAlt, FaCog, FaLink, FaQrcode } from 'react-icons/fa';
import DropdownField from '../../../shared/DropdownField';
import TextInput from '../../../shared/TextInput';
import { fetchData } from '../../../helpers/fetchData';
import toast from 'react-hot-toast';

function WhatsappQr({ updateWhatsapp }) {
    const { t } = useTranslation();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [qrCode, setQrCode] = useState(null);
    const [selectedService, setSelectedService] = useState('my_whatsapp_cloud');
    const [accessToken, setAccessToken] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const serviceOptions = [
        {
            label: 'My WhatsApp Cloud',
            value: 'my_whatsapp_cloud',
        },
    ];

    const handleGenerateQR = async () => {
        if (!accessToken.trim()) {
            toast.error(t('Please enter access token'));
            return;
        }

        try {
            setLoading(true);
            const response = await fetchData('POST', '/v3/create_whatsapp_instance', {
                service: selectedService,
                access_token: accessToken,
            });

            if (response?.qrCodeBase64) {
                setQrCode(response.qrCodeBase64);
                setStep(2);
            }
        } catch (error) {
            console.error('Error generating QR code:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = () => {
        updateWhatsapp();
    };

    return (
        <div className="p-4">
            {step === 1 ? (
                <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <DropdownField
                            label={t('WhatsApp Service')}
                            useFormik={false}
                            options={serviceOptions}
                            value={selectedService}
                            onChange={(e) => setSelectedService(e.target.value)}
                            placeholder={t('Select service')}
                            disabled={loading}
                        />

                        <div className="relative">
                            <TextInput
                                label={t('Access Token')}
                                useFormik={false}
                                value={accessToken}
                                onChange={(e) => setAccessToken(e.target.value)}
                                placeholder={t('Enter your WhatsApp access token')}
                                disabled={loading}
                                type={showPassword ? 'text' : 'password'}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-9 text-gray-500 hover:text-gray-700 transition-colors"
                                disabled={loading}
                            >
                                {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                            </button>
                        </div>
                    </div>

                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-5">
                        <h3 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
                            <FaLink className="text-blue-600" />
                            {t('How to Get Your Access Token')}
                        </h3>
                        <div className="space-y-3 text-sm text-gray-700">
                            <div className="flex items-start gap-3">
                                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">1</span>
                                <div>
                                    <p className="font-medium text-gray-800">{t('Visit MyWhats Cloud')}</p>
                                    <p className="text-gray-600 mt-1">
                                        {t('Go to')}{' '}
                                        <a
                                            href="https://mywhats.cloud"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:text-blue-700 underline font-medium"
                                        >
                                            mywhats.cloud
                                        </a>
                                        {' '}{t('and log in to your account')}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">2</span>
                                <div>
                                    <p className="font-medium text-gray-800">{t('Navigate to API Settings')}</p>
                                    <p className="text-gray-600 mt-1">{t('Go to Dashboard → API Settings or Integration section')}</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">3</span>
                                <div>
                                    <p className="font-medium text-gray-800">{t('Generate or Copy Access Token')}</p>
                                    <p className="text-gray-600 mt-1">{t('Click on "Generate Token" or copy your existing access token')}</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">4</span>
                                <div>
                                    <p className="font-medium text-gray-800">{t('Paste Token Above')}</p>
                                    <p className="text-gray-600 mt-1">{t('Copy the token and paste it in the Access Token field above')}</p>
                                </div>
                            </div>
                        </div>
                        {/* <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                            <p className="text-xs text-yellow-800 flex items-start gap-2">
                                <span className="text-yellow-600 mt-0.5">⚠️</span>
                                <span>{t('Keep your access token secure and do not share it with anyone. It provides full access to your WhatsApp integration.')}</span>
                            </p>
                        </div> */}
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t">
                        <CustomButton
                            label={t('Generate QR Code')}
                            icon={<FaQrcode className="mr-2" />}
                            onClick={handleGenerateQR}
                            disabled={loading || !accessToken.trim()}
                            loading={loading}
                            buttonType="primary"
                        />
                    </div>
                </div>
            ) : (
                <div className="space-y-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h3 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
                            <FaLink className="text-blue-600" />
                            {t('Link Your WhatsApp Device')}
                        </h3>
                        <div className="space-y-2 text-sm text-gray-700">
                            <div className="flex items-start gap-3">
                                <FaMobileAlt className="text-blue-600 mt-0.5 flex-shrink-0" size={16} />
                                <span>{t('Open WhatsApp on your phone')}</span>
                            </div>
                            <div className="flex items-start gap-3">
                                <FaCog className="text-blue-600 mt-0.5 flex-shrink-0" size={16} />
                                <span>{t('Go to Settings → Linked Devices → Link a Device')}</span>
                            </div>
                            <div className="flex items-start gap-3">
                                <FaQrcode className="text-blue-600 mt-0.5 flex-shrink-0" size={16} />
                                <span>{t('Scan the QR code below')}</span>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-center items-center p-4 min-h-[300px]">
                        {loading ? (
                            <div className="w-[250px] h-[250px] relative bg-gray-50 rounded-lg p-2 overflow-hidden border border-gray-100">
                                <div className="w-full h-full grid grid-cols-6 gap-2 pt-1 pl-1">
                                    {Array.from({ length: 36 }).map((_, i) => (
                                        <Skeleton
                                            key={i}
                                            className="w-full h-full opacity-30"
                                            borderRadius="4px"
                                        />
                                    ))}
                                </div>
                            </div>
                        ) : qrCode ? (
                            <div className="border-4 border-green-500 rounded-lg p-2 shadow-lg">
                                <img
                                    src={qrCode}
                                    alt="WhatsApp QR Code"
                                    className="w-[250px] h-[250px]"
                                />
                            </div>
                        ) : (
                            <p className="text-gray-500">{t('No QR code available')}</p>
                        )}
                    </div>

                    <div className="flex justify-between gap-3 pt-4 border-t">
                        <CustomButton
                            label={t('Back')}
                            icon={<FaArrowRight className="mr-2 rotate-180" />}
                            onClick={() => setStep(1)}
                            buttonType="secondary"
                            disabled={loading}
                        />
                        <CustomButton
                            label={t('Complete Integration')}
                            icon={<FaCheckDouble className="mr-2" />}
                            onClick={handleUpdate}
                            buttonType="primary"
                            disabled={loading}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

export default WhatsappQr;

